import { Head, Link } from '@inertiajs/react'
import { useState } from 'react'
import { Copy, ExternalLink } from 'lucide-react'

import { AppShell } from '@/layouts/AppShell'
import { PageHeader } from '@/components/layout/PageHeader'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import type { Breadcrumb } from '@/types/shared'

type SchoolDetail = {
  id: string
  name: string
  prefecture?: string | null
  league?: string | null
  createdAt: string
  updatedAt: string
  selfIntroduction?: string | null
  coachesCount: number
  contactsCount: number
}

type AdminSchoolShowProps = {
  school: SchoolDetail
  registrationLink: string
  breadcrumbs: Breadcrumb[]
}

export default function AdminSchoolShow({ school, registrationLink, breadcrumbs }: AdminSchoolShowProps) {
  const [copied, setCopied] = useState(false)

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(registrationLink)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (error) {
      console.error('リンクのコピーに失敗しました', error)
    }
  }

  return (
    <AppShell>
      <Head title={`高校詳細｜${school.name}`} />
      <PageHeader
        title={school.name}
        description="高校マスターの詳細を確認できます。"
        breadcrumbs={breadcrumbs}
        actions={
          <Button asChild variant="secondary" size="sm">
            <Link href={`/admin/schools/${school.id}/edit`}>編集</Link>
          </Button>
        }
      />

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <Card className="space-y-6" padding="lg">
          <Section title="基本情報">
            <InfoGrid
              items={[
                { label: '高校名', value: school.name },
                { label: '都道府県', value: school.prefecture ?? '未設定' },
                { label: '所属リーグ', value: school.league ?? '未設定' },
                { label: '登録日時', value: formatDateTime(school.createdAt) },
                { label: '更新日時', value: formatDateTime(school.updatedAt) },
              ]}
            />
          </Section>

          {school.selfIntroduction ? (
            <Section title="自己紹介">
              <div className="rounded-2xl border border-white/10 bg-white/5 p-4 text-sm text-white/80 whitespace-pre-wrap">
                {school.selfIntroduction}
              </div>
            </Section>
          ) : null}

          <Section title="関連データ">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <KpiCard label="登録コーチ数" value={school.coachesCount} />
              <KpiCard label="コンタクト数" value={school.contactsCount} />
            </div>
          </Section>

          <Section title="高校コーチ登録リンク">
            <div className="flex items-center gap-3">
              <input
                value={registrationLink}
                readOnly
                className="flex-1 rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-sm text-white/80"
              />
              <Button type="button" variant="secondary" size="sm" onClick={copyLink}>
                <Copy className="mr-2 h-4 w-4" />
                {copied ? 'コピー済み' : 'コピー'}
              </Button>
            </div>
          </Section>
        </Card>

        <Card padding="lg" className="space-y-4">
          <h2 className="text-lg font-semibold text-white">ショートカット</h2>
          <div className="space-y-3 text-sm">
            <ShortcutLink href={`/school`} label="高校ダッシュボードを確認" />
            <ShortcutLink href={`/admin/players?school_id=${school.id}`} label="高校からのコンタクトを確認" />
          </div>
        </Card>
      </div>
    </AppShell>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-4">
      <h2 className="text-lg font-semibold text-white">{title}</h2>
      {children}
    </section>
  )
}

function InfoGrid({ items }: { items: Array<{ label: string; value: React.ReactNode }> }) {
  return (
    <dl className="grid grid-cols-1 gap-4 md:grid-cols-2">
      {items.map((item) => (
        <div key={item.label} className="rounded-2xl border border-white/10 bg-white/5 p-4">
          <dt className="text-xs uppercase tracking-wide text-white/40">{item.label}</dt>
          <dd className="mt-2 text-sm text-white/80">{item.value}</dd>
        </div>
      ))}
    </dl>
  )
}

function KpiCard({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
      <p className="text-xs uppercase tracking-wider text-white/40">{label}</p>
      <p className="mt-2 text-3xl font-semibold text-white">{value.toLocaleString()}</p>
    </div>
  )
}

function ShortcutLink({ href, label }: { href: string; label: string }) {
  return (
    <Link
      href={href}
      className="group flex items-center justify-between rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-white/80 transition hover:border-white/20 hover:bg-white/10"
    >
      <span>{label}</span>
      <ExternalLink className="h-4 w-4 text-white/40 group-hover:text-white/60" />
    </Link>
  )
}

function formatDateTime(value: string) {
  return new Date(value).toLocaleString('ja-JP', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}
